/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect } from 'react';
import Divider from '../../../components/shared/Divider';
import BadgeNotification from '../../../components/shared/BadgeNotification';
import ListingCard from '../../../components/shared/ListingCard/ListingCard';
import { gql, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

const AUCTION_BADGES = gql`
  query AuctionBadges($listingIds: [ID!]!) {
    auctionBadges(listingIds: $listingIds)
  }
`;

const AUCTIONS_UI_ENABLED = process.env.REACT_APP_AUCTIONS_ENABLED === 'true';

// One bulk call per result set; a small overlay chip marks auctioned listings.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useAuctionBadges = (items: any[]) => {
  const ids = (items || [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((d: any) => d && d.id && d.type !== 'storeFront')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((d: any) => Number(d.id));
  const { data } = useQuery(AUCTION_BADGES, {
    variables: { listingIds: ids },
    skip: !AUCTIONS_UI_ENABLED || ids.length === 0,
    fetchPolicy: 'cache-and-network'
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map: { [key: string]: any } = {};
  for (const b of data?.auctionBadges || []) {
    map[String(b.listingId)] = b;
  }
  return map;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AuctionBadgeChip = ({ badge }: { badge: any }) => {
  if (!badge) {
    return null;
  }
  return (
    <span className="pointer-events-none absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-gray-900 shadow-sm">
      <FormattedMessage id="page.marketplace.auction_badge" defaultMessage="Auction" />
      {badge.status === 'live' ? (
        <>
          {' '}
          · {badge.currency === 'usd' ? '$' : 'Đ'}
          {badge.currentPrice}
        </>
      ) : null}
    </span>
  );
};

import { LISTING_BREAKPOINT, LIST_PAGES, NUMBER_OF_ITEMS_PER_PAGE } from '../../../constants/data';
import StorefrontItemCard from '../../../components/shared/ListingCard/StorefrontItemCard';
import StorefrontCard from '../../../components/shared/ListingCard/StorefrontCard';
import useDeviceDetect from '../../../hooks/useDeviceDetect';
import { useIntl } from 'react-intl';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../lib/redux/hooks';
import { addFilterModal, setHoveredListing } from '../../../lib/redux/reducers/filter.slice';
import { dataTypes } from '../../../util/MoreFilterUtil';

// Hover sync applies only where hover exists (map plan F4) - touch devices
// skip the listeners entirely.
const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;
import { useHandleSave } from '../../../hooks/useHandleSave';
import Loader from '../../../components/shared/Loader';

type Props = {
  listings: any;
  storefronts: any;
  personalListingsCount: number;
  storefrontsCount: number;
  wantedListingsCount: number;
  storefrontItemsCount: number;
  eventsCount: number;
  onLoadMore: () => void;
  loadingMore: boolean;
  refetch: () => void;
  loading: boolean;
};

const SearchResult: FC<Props> = ({
  listings,
  storefronts,
  personalListingsCount,
  storefrontsCount,
  wantedListingsCount,
  storefrontItemsCount,
  eventsCount,
  onLoadMore,
  loadingMore,
  refetch,
  loading
}) => {
  const { save } = useHandleSave(refetch);
  const { isMobile } = useDeviceDetect();

  const intl = useIntl();
  const dispatch = useDispatch();
  // Chips as filters (map plan E3): tapping a chip toggles its listing type
  // via the SAME moreFilter.listings array the More-filters checkboxes use.
  // No listings array in redux means "all types".
  const filterFromModal = useAppSelector((state) => state.filter.filterModal);
  const selectedTypeOptions = filterFromModal?.moreFilter?.listings ?? dataTypes;
  const isTypeActive = (value: string) =>
    selectedTypeOptions.some((option: any) => option.value === value);
  const toggleType = (value: string) => {
    const current = filterFromModal?.moreFilter?.listings ?? dataTypes;
    let next = current.some((option: any) => option.value === value)
      ? current.filter((option: any) => option.value !== value)
      : [...current, dataTypes.find((option) => option.value === value)];
    // Turning off the LAST active type cycles back to all types - an empty
    // map from an empty selection reads as a bug, not a filter.
    if (next.length === 0) {
      next = dataTypes;
    }
    dispatch(
      addFilterModal({
        filterModal: {
          ...filterFromModal,
          moreFilter: { ...(filterFromModal?.moreFilter ?? {}), listings: next }
        }
      })
    );
  };
  const chipClass = (value: string) =>
    `rounded-2xl transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 ${
      isTypeActive(value) ? '' : 'opacity-50'
    }`;
  // Page-by-page order (Load more fix). Each fetch brings one page of
  // listings and one page of shops. Sorting the WHOLE list by date (oldest
  // first) scattered every newly loaded page among the cards already on
  // screen - most landed at the very top, far from the button - so Load more
  // looked dead. A card's page comes from its position in the cache's
  // append-only lists; within a page, newest first (the server pages
  // newest first too), so each Load more adds its cards at the bottom.
  const pageOf = (index: number) => Math.floor(index / NUMBER_OF_ITEMS_PER_PAGE);
  const timeOf = (item: any) => new Date(item?.publishedAt).getTime() || 0;
  const mapData = [
    ...(listings || []).map((item: any, index: number) => ({ item, page: pageOf(index) })),
    ...(storefronts || []).map((item: any, index: number) => ({ item, page: pageOf(index) }))
  ]
    .sort((a, b) => a.page - b.page || timeOf(b.item) - timeOf(a.item))
    .map(({ item }) => item);

  // List<->pin hover sync (map plan F4). Cards announce hover with source
  // 'list' (lights the pin); pins announce with source 'map' (rings the card
  // and scrolls it into view). Composite type-id key - id spaces collide.
  const hoveredKey = useAppSelector((state) => state.filter.hoveredListing?.key ?? null);
  const hoveredSource = useAppSelector((state) => state.filter.hoveredListing?.source ?? null);
  useEffect(() => {
    if (hoveredKey && hoveredSource === 'map') {
      document
        .getElementById(`map-card-${hoveredKey}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [hoveredKey, hoveredSource]);
  const hoverWrapProps = (cardKey: string) => ({
    id: `map-card-${cardKey}`,
    onMouseEnter: () => {
      if (canHover) {
        dispatch(setHoveredListing({ key: cardKey, source: 'list' }));
      }
    },
    onMouseLeave: () => {
      if (canHover) {
        dispatch(setHoveredListing(null));
      }
    },
    className: `relative rounded-2xl transition-shadow ${
      hoveredKey === cardKey && hoveredSource === 'map' ? 'ring-2 ring-yellow-400' : ''
    }`
  });
  const auctionBadgeMap = useAuctionBadges(listings || []);

  const totalItems =
    personalListingsCount +
    wantedListingsCount +
    storefrontItemsCount +
    eventsCount +
    storefrontsCount;
  const hasMore = mapData.length < totalItems;
  const listingsText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.LISTINGS.id,
    defaultMessage: TRANSLATED_CONSTANTS.LISTINGS.defaultMessage
  });
  const personalText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.PERSONAL.id,
    defaultMessage: TRANSLATED_CONSTANTS.PERSONAL.defaultMessage
  });
  const wantedText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.WANTED.id,
    defaultMessage: TRANSLATED_CONSTANTS.WANTED.defaultMessage
  });
  const storefrontsText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.STOREFRONTS.id,
    defaultMessage: TRANSLATED_CONSTANTS.STOREFRONTS.defaultMessage
  });
  const storefrontsItemsText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.STOREFRONT_ITEMS.id,
    defaultMessage: TRANSLATED_CONSTANTS.STOREFRONT_ITEMS.defaultMessage
  });
  const eventsText = intl.formatMessage({
    id: TRANSLATED_CONSTANTS.EVENTS.id,
    defaultMessage: TRANSLATED_CONSTANTS.EVENTS.defaultMessage
  });
  const wantedListingsText = isMobile ? wantedText : wantedText + ' ' + listingsText;
  const personalListingsText = isMobile ? personalText : personalText + ' ' + listingsText;

  return (
    <div className="">
      {/* Chips: gray at zero (B3), tap to toggle the type on the map AND the
          list (E3). A toggled-off type also drops out of the counts, so the
          headline stays consistent with what's shown. */}
      <div className="flex justify-start items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => toggleType('personal')}
          aria-pressed={isTypeActive('personal')}
          className={chipClass('personal')}>
          <BadgeNotification
            theme="light"
            color={personalListingsCount > 0 && isTypeActive('personal') ? 'blue' : 'gray'}
            title={personalListingsCount.toString()}
            message={personalListingsText}
          />
        </button>
        <button
          type="button"
          onClick={() => toggleType('storefront')}
          aria-pressed={isTypeActive('storefront')}
          className={chipClass('storefront')}>
          <BadgeNotification
            theme="light"
            color={storefrontsCount > 0 && isTypeActive('storefront') ? 'purple' : 'gray'}
            title={storefrontsCount.toString()}
            message={storefrontsText}
          />
        </button>
        <button
          type="button"
          onClick={() => toggleType('wanted')}
          aria-pressed={isTypeActive('wanted')}
          className={chipClass('wanted')}>
          <BadgeNotification
            theme="light"
            color={wantedListingsCount > 0 && isTypeActive('wanted') ? 'pink' : 'gray'}
            title={wantedListingsCount.toString()}
            message={wantedListingsText}
          />
        </button>
        <button
          type="button"
          onClick={() => toggleType('storefrontItem')}
          aria-pressed={isTypeActive('storefrontItem')}
          className={chipClass('storefrontItem')}>
          <BadgeNotification
            theme="light"
            color={storefrontItemsCount > 0 && isTypeActive('storefrontItem') ? 'indigo' : 'gray'}
            title={storefrontItemsCount.toString()}
            message={storefrontsItemsText}
          />
        </button>
        <button
          type="button"
          onClick={() => toggleType('event')}
          aria-pressed={isTypeActive('event')}
          className={chipClass('event')}>
          <BadgeNotification
            theme="light"
            color={eventsCount > 0 && isTypeActive('event') ? 'orange' : 'gray'}
            title={eventsCount.toString()}
            message={eventsText}
          />
        </button>
      </div>
      {loading && mapData.length === 0 ? (
        // Nothing to show yet (first load) - full spinner.
        <Loader className="h-[400px] flex justify-center items-center " />
      ) : (
        // Refetches keep the previous results on screen (see the previousData
        // fallback in useGetMarketPlaceData) and just dim them briefly.
        <div
          className={`my-4 pb-4 flex flex-col gap-4 min-h-[300px] transition-opacity duration-200 ${
            loading ? 'opacity-60 pointer-events-none' : ''
          }`}>
          {mapData.map((data: any, index: any) => {
            const cardKey = `${data?.type}-${data?.id}`;
            if (data?.type === 'personal' || data?.type === 'wanted' || data?.type === 'event') {
              return (
                <div key={index} {...hoverWrapProps(cardKey)}>
                  <AuctionBadgeChip badge={auctionBadgeMap[String(data?.id)]} />
                  <ListingCard
                    cardClickable={true}
                    listing={data}
                    page={LIST_PAGES.MAP}
                    size={LISTING_BREAKPOINT.DEFAULT}
                    onLike={save}></ListingCard>
                </div>
              );
            } else if (data?.type === 'storefrontItem') {
              return (
                <div key={index} {...hoverWrapProps(cardKey)}>
                  <StorefrontItemCard
                    cardClickable={true}
                    listing={data}
                    storeFront={data.storefront}
                    page={LIST_PAGES.MAP}
                    size={LISTING_BREAKPOINT.DEFAULT}
                    onLike={save}></StorefrontItemCard>
                </div>
              );
            } else {
              return (
                <div key={index} {...hoverWrapProps(cardKey)}>
                  <StorefrontCard
                    cardClickable={true}
                    storeFront={data}
                    page={LIST_PAGES.MAP}
                    size={LISTING_BREAKPOINT.DEFAULT}
                    onLike={save}
                  />
                </div>
              );
            }
          })}
        </div>
      )}
      <Divider />
      {hasMore && (
        <div className="flex justify-center pt-4 pb-6">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="w-full rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 md:w-auto">
            {loadingMore ? (
              <FormattedMessage id="shared.loading" defaultMessage="Loading..." />
            ) : (
              <FormattedMessage id="shared.load_more" defaultMessage="Load more" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResult;
